import { WizIcon, WizPopUp, WizTranslationManager } from '@wizeoo/akm-component-lib';
import { WizMathUtils } from '@wizeoo/akm-core-lib';
import { IncExcKey, TranslatedEmb } from '@wizeoo/akm-message-lib';
import { ChronocodeVO } from '../core/chronocode.vo';
import { IncExcContentObj } from '../core/tool/incex.content.obj';
import { IncExcMainObj } from '../core/tool/incex.main.obj';
import { ViewData } from '../../model/view.data';
import { TranslatableLabelVO } from '../common/translation/translatable-label.vo';
import { entity, field } from '@wizeoo/akm-compute-lib';
import { Model } from '../../model/model';

/** VO clés include / exclude */
@entity('wizeoo.allkeyme.core.message.IncExcKey', 'BU')
export class IncExcKeyVO {
  
  protected _model: Model = null;
  public get model(): Model {
    return this._model as Model;
  }
  public set model(value: Model) {
      this._model = value;
  }
  
  constructor(model: Model, pSource?: IncExcKey) {
    this._model = model;
    this._source = null;
    if (pSource) {
      this._source = pSource;
    } else {
      this._source = new IncExcKey();
      this._source.uid = WizMathUtils.createUUID(); // génération du UID si nouveau
    }
  }

  /** Le VO source provenant du back */
  private _source: IncExcKey;


  public set source(value: IncExcKey) {
    // on écrase le translatableVO pour régénération
    this._descriptionVO = null;
    this._label = null;
    this._source = value;
  }
  public get source(): IncExcKey {
    return this._source;
  }

  public set uid(uid: string) {
    this._source.uid = uid;
  }
  public get uid(): string {
    return this._source.uid;
  }

  // Id technique
  public get id(): number {
    return this._source.id;
  }
  public set id(value: number) {
    this._source.id = value;
  }

  public set domain(domain: string) {
    this._source.domain = domain;
  }

  public get domain(): string {
    return this._source.domain;
  }

  public set businessUnitCode(value: string) {
    this._source.businessUnitCode = value;
  }

  public get businessUnitCode(): string {
    return this._source.businessUnitCode;
  }

  @field('string')
  public set key(key: string) {
    this._source.key = key;
  }
  public get key(): string {
    return this._source.key;
  }

  @field('boolean')
  public get inc(): boolean {
    return this._source.inc;
  }
  public set inc(value: boolean) {
    this._source.inc = value;
  }

  @field('TranslatedEmb')
  public set description(value: TranslatedEmb) {
    this._source.description = value;
  }
  public get description(): TranslatedEmb {
    return this._source.description;
  }

  @field('boolean')
  public get deprecated(): boolean {
    return this._source.deprecated;
  }
  public set deprecated(value: boolean) {
    this._source.deprecated = value;
  }

  @field('boolean')
  public get deleted(): boolean {
    return this._source.deleted;
  }
  public set deleted(value: boolean) {
    this._source.deleted = value;
  }

  public set visionCode(visionCode: string) {
    this._source.visionCode = visionCode;
  }

  public get visionCode(): string {
    return this._source.visionCode;
  }

  public get version(): number {
    // technique
    return this._source.version;
  }
  public set version(value: number) {
    this._source.version = value;
  }

  /* ################################################
                    Accesseurs composites
  ################################################ */

  /**
   * l'objet existe en BDD (id)
   */
  public get exist(): boolean {
    return this.id > 0;
  }

  public from: IncExcKeyVO = null; // sur persistence en update, permet de maintenir le lien avec l'élément qu'on va supprimer, pour synchro sur callback de commit

  /** description en mode VO */
  private _descriptionVO: TranslatableLabelVO;
  public get descriptionVO(): TranslatableLabelVO {
    if (!this._descriptionVO) {
      this._descriptionVO = this.model.localeEngine.newTranslatableVO(this, 'description');
    }
    return this._descriptionVO;
  }

  /** clé d'unicité (dans la BU) */
  public get uniqueKey(): string {
    // visionCode | Key | inc
    return [this.visionCode, this.key, this.inc].join(ViewData.KEY_SEPARATOR);
  }

  /** retourne soit le code de la vision, soit rien (à la BU) */
  public get visionCodeStr(): string {
    let result: string = this.businessUnitCode;
    if (!!this.visionCode) {
      result = this.visionCode;
    }
    return result;
  }

  /**
   *  accès à la clé sous forme de map FDVCODE/CHRONOCODE # level = IncExcContentObj
   * CHRONO#level = value
   * FD#level = value
   */
  private _keyMap: Map<string, IncExcContentObj> = null;
  public get keyMap(): Map<string, IncExcContentObj> {
    if (!this._keyMap) {
      this._keyMap = new Map<string, IncExcContentObj>();
      if (!!this.key) {
        const keyParts: string[] = this.key.split(ViewData.KEY_SEPARATOR); // split sur '|'
        keyParts.forEach((part: string) => {
          const subParts: string[] = part.split(ViewData.CONTEXT_SEPARATOR); // sur sur #, donne FD ou CHRONO | level | value

          let codePart: string = subParts.slice(0, 1).join(ViewData.CONTEXT_SEPARATOR);
          // attention, si c'est un chrono, c'est CHRONO#level, ce qui n'est pas parlant.
          if (subParts[0] === ViewData.CHRONO) {
            // on cherche le chono de niveau 1 correspondant
            // console.log('parts', subParts);
            const chrono: ChronocodeVO = this.model.chronocodeEngine.getLevelOne(subParts[2]);
            if (!!chrono) {
              codePart = [chrono.code, chrono.level].join(ViewData.CONTEXT_SEPARATOR);
            } // sinon, c'est une erreur de structure (chronocode existe pas)
          }

          const sourceObj: IncExcContentObj = this.model.incExcKeyEngine.getKeyPart(codePart, subParts[2]);
          if (!sourceObj) {
            const engineKey: Map<string, IncExcMainObj> = this.model.incExcKeyEngine.keyPartsMap;
            console.log('noSource', keyParts, engineKey);
          }

          this._keyMap.set(subParts.slice(0, 2).join(ViewData.CONTEXT_SEPARATOR), sourceObj);
        });
      }
      this.parts = Array.from(this.keyMap.values()); // alimentation de la collection
    }
    return this._keyMap;
  }
  public set keyMap(value: Map<string, IncExcContentObj>) {
    this._keyMap = value;
  }

  /** accesseur sur les clés pour rendu dans le générateur, alimenté par keyMap  */
  private _parts: IncExcContentObj[] = [];
  public get parts(): IncExcContentObj[] {
    return this._parts;
  }
  public set parts(value: IncExcContentObj[]) {
    this._parts = Array.from(value);
  }

  /** reconstruction de la clé depuis la map (update / create) */
  private buildKey() {
    const resArr: string[] = [];
    Array.from(this.keyMap.keys())
      .sort((a, b) => a.localeCompare(b))
      .forEach((s: string) => {
        resArr.push(s + ViewData.CONTEXT_SEPARATOR + this.keyMap.get(s).valuePart);
      });
    this.key = resArr.join(ViewData.KEY_SEPARATOR);
  }

  /** ajout d'un élément à la pile.
   * ajout à KeyMap et reconstruction de la clé
   */
  public addPart(part: IncExcContentObj): void {
    this.keyMap.set(part.codePart, part);
    this.parts.push(part);
    this._label = null; // on rince le label pour reset
    this.buildKey();
    // TODO buildKey attention, unicité, et delete insert
  }

  /** suppression d'un élément.
   * reconstruction de la clé et ajout à KeyMap
   */
  public removePart(part: IncExcContentObj): void {
    this.keyMap.delete(part.codePart);
    this.parts.splice(this.parts.indexOf(part), 1);
    this._label = null; // on rince le label pour reset
    this.buildKey();
    // TODO buildKey attention, unicité, et delete insert
  }

  /** libellé intelligible */
  private _label: string = null;
  public get label(): string {
    if (!this._label) {
      const result: string[] = [];
      Array.from(this.keyMap.keys())
        .sort((a, b) => a.localeCompare(b))
        .forEach((key: string) => {
          const ioco: IncExcContentObj = this.keyMap.get(key);
          let part: string = '';
          if (!!ioco && !!ioco.label) {
            part = ioco.parent.label + ': ' + ioco.label;
          } else {
            // pas de parent ??
            console.log('no ioco', ioco);
          }
          result.push(part);
        });
      this._label = result.join('; ');
    }
    return this._label;
  }

  public reset(): void {
    this._label = null;
    this._keyMap = null;
  }

  /**
   * Return translated desc
   * @return string
   */
  get trsDesc(): string {
    return this.model.localeEngine.translate(this.description);
  }

  /** redu way intelligible */
  public get incStr(): string {
    let result: string = WizTranslationManager.getInstance().translateKey('$.incexc.include.$');
    if (!this.inc) {
      result = WizTranslationManager.getInstance().translateKey('$.incexc.exclude.$');
    }
    return result;
  }

  /** contrôle de cohérence de la structure */
  public checkParts(newPart: IncExcContentObj): boolean {
    if (this.keyMap.has(newPart.codePart)) {
      // niveau déjà présent
      WizPopUp.toast('$.incexc.field.exist.$', WizIcon.TOAST_ERROR).show();
      return false;
    }

    let hierMismatch: boolean = false;
    Array.from(this.keyMap.values()).forEach((exist: IncExcContentObj) => {
      if (exist.mainCodePart === newPart.mainCodePart) {
        if (exist.level < newPart.level && !newPart.valuePart.startsWith(exist.valuePart)) {
          hierMismatch = true; // level supétieur (plus petit nombre), mais la nouvelle part ne le reconnait pas comme parent
        }
        if (exist.level > newPart.level && !exist.valuePart.startsWith(newPart.valuePart)) {
          hierMismatch = true; // level inféreieur (plus grand nombre), mais la nouvelle part n'est pas un enfant
        }
      }
    });
    if (hierMismatch) {
      WizPopUp.toast('$.incexc.field.mismatch.$', WizIcon.TOAST_ERROR).show();
      return false;
    }

    return true;
  }
}
